import { useLazyQuery } from '@apollo/client';
import {
  GetCommentsByConversationIdDocument,
  type GetCommentsByConversationIdQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Imperative (lazy) read of the comments for one conversation — RSP-6214.
 * The editor's tinycomments integration (`useComments.tinycomments_lookup`)
 * calls this on demand and awaits a fresh (no-cache) result, so the
 * declarative `createQueryHook` factory does not fit. Both data paths live
 * here so the consumer never touches Apollo directly.
 *
 * - trpc ON  → `queryClient.fetchQuery` with `staleTime: 0` forces a fresh
 *   fetch, then maps the tRPC rows back to the GraphQL `comment` row shape so
 *   the downstream tinycomments mapping stays unchanged.
 * - trpc OFF → the legacy Apollo `useLazyQuery` with `fetchPolicy: 'no-cache'`.
 *
 * The hook intentionally THROWS on a real error — the tinycomments `fail(e)`
 * callback handles it.
 */
export const useGetCommentsByConversationId = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [getCommentsByConversationId] = useLazyQuery(
    GetCommentsByConversationIdDocument,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const getComments = useCallback(
    async (
      conversationId: string
    ): Promise<GetCommentsByConversationIdQuery['comment']> => {
      if (trpcEnabled) {
        const rows = await queryClient.fetchQuery({
          ...trpc.frontend.comment.commentsByConversationId.queryOptions({
            conversationId,
          }),
          staleTime: 0,
        });

        return rows.map((row) => ({
          Id: row.Id,
          Content: row.Content,
          ModifiedAtTimestamp: row.ModifiedAtTimestamp,
          CreatedAtTimestamp: row.CreatedAtTimestamp,
          CreatedByUser: row.CreatedByUser,
          ModifiedByUser: row.ModifiedByUser,
          createdByUser: row.createdByUser
            ? { FriendlyName: row.createdByUser.FriendlyName }
            : null,
          modifiedByUser: row.modifiedByUser
            ? { FriendlyName: row.modifiedByUser.FriendlyName }
            : null,
        }));
      }

      const result = await getCommentsByConversationId({
        variables: { ConversationId: conversationId },
      });

      return result.data?.comment ?? [];
    },
    [trpcEnabled, queryClient, trpc, getCommentsByConversationId]
  );

  return { getComments };
};
